import { useCallback } from 'react';
import { useDataProvider } from 'react-admin';

import { useErrorHandling } from '@app/hooks/useErrorHandling';
import { FIELD_TYPES, formatNonRequiredFields, validateFormFields } from '@app/utils/validateUtils';
import { Divider } from '@mui/material';

import { DefaultForm } from '@app/components/common/DefaultForm';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { ACTION_MODE } from '../ListTaxonomies';
import { TaxonomyBreadCrumb } from './TaxonomyBreadCrumb';
import { TaxonomyForm } from './TaxonomyForm';
import { TaxonomyInfo } from './TaxonomyInfo';
import { TaxonomyReorder } from './TaxonomyReorder';

interface TaxonomyDetailsProps {
  actionMode: string | null;
  selectedTaxonomy: GenericObject | null;
  onDelete(row: GenericObject): void;
  handleChangeActionMode(action: string): void;
  handleShowInfo(taxonomy: GenericObject | null): void;
}

const nonRequiredFields = [
  { key: 'seo_title', emptyValue: '', isMultilingual: true },
  { key: 'meta_description', emptyValue: '', isMultilingual: true },
  { key: 'nav_label', emptyValue: '', isMultilingual: true },
  { key: 'related_topic_ids', emptyValue: [] },
  { key: 'parent_category_id', emptyValue: null },
];

const formFields = [
  { key: 'category_name', type: FIELD_TYPES.MULTILINGUAL, message: 'Category Name is required' },
  { key: 'description', type: FIELD_TYPES.MULTILINGUAL, message: 'Description is required' },
];

export const TaxonomyDetails = ({
  actionMode,
  onDelete,
  handleChangeActionMode,
  handleShowInfo,
  selectedTaxonomy,
}: TaxonomyDetailsProps) => {
  const dataProvider = useDataProvider();
  const [handleError] = useErrorHandling();

  const transformCreate = (values: GenericObject) => {
    formatNonRequiredFields(values, nonRequiredFields);

    if (selectedTaxonomy) {
      return {
        ...values,
        parent_category_id: selectedTaxonomy?.id,
        level:
          selectedTaxonomy?.level !== undefined && selectedTaxonomy?.level !== null
            ? (selectedTaxonomy.level as number) + 1
            : null,
      };
    }

    return { ...values, level: 1 };
  };

  const transformUpdate = (values: GenericObject) => {
    formatNonRequiredFields(values, nonRequiredFields);

    return values;
  };

  const handleClose = () => {
    handleShowInfo(null);
    handleChangeActionMode(ACTION_MODE.DEFAULT);
  };

  const onUpdate = async (values: GenericObject) => {
    if (!selectedTaxonomy) {
      return;
    }

    try {
      const response = await dataProvider.update('taxonomies', {
        id: selectedTaxonomy.id as number,
        data: transformUpdate(values),
        previousData: selectedTaxonomy,
      });

      handleShowInfo(response?.data);
      handleChangeActionMode(ACTION_MODE.DEFAULT);
    } catch (error: any) {
      handleError(error);
    }
  };

  const onCreate = async (values: GenericObject) => {
    try {
      const response = await dataProvider.create('taxonomies', {
        data: transformCreate(values),
      });

      handleShowInfo(response?.data);
      handleChangeActionMode(ACTION_MODE.DEFAULT);
    } catch (error: any) {
      handleError(error);
    }
  };

  const validate = useCallback((values: GenericObject) => validateFormFields({ fields: formFields, values }), []);

  return (
    <div style={{ paddingLeft: '15px' }}>
      <div style={{ paddingLeft: '50px' }}>
        <p style={{ fontSize: '20px', fontWeight: 'bold' }}>PROPERTIES</p>
        <TaxonomyBreadCrumb selectedTaxonomy={selectedTaxonomy} />
      </div>
      <Divider />
      {!!(selectedTaxonomy && actionMode === ACTION_MODE.DEFAULT) && (
        <TaxonomyInfo
          onDelete={onDelete}
          handleChangeActionMode={handleChangeActionMode}
          selectedTaxonomy={selectedTaxonomy}
        />
      )}
      {!!(selectedTaxonomy && actionMode === ACTION_MODE.EDIT) && (
        <div style={{ padding: '15px 35px' }}>
          <DefaultForm
            hideDelete
            validate={validate}
            onSubmit={onUpdate}
            record={selectedTaxonomy}
            onClose={handleClose}
            resourceSingular="taxonomy">
            <TaxonomyForm selectedTaxonomy={selectedTaxonomy} />
          </DefaultForm>
        </div>
      )}

      {actionMode === ACTION_MODE.CREATE && (
        <div style={{ padding: '15px 35px' }}>
          <DefaultForm
            onSubmit={onCreate}
            validate={validate}
            defaultValues={selectedTaxonomy ? { parent_category_id: selectedTaxonomy.id } : {}}
            onClose={handleClose}
            hideDelete
            resourceSingular="taxonomy">
            <TaxonomyForm creating />
          </DefaultForm>
        </div>
      )}

      {actionMode === ACTION_MODE.REORDER && (
        <div style={{ padding: '15px 35px' }}>
          <TaxonomyReorder selectedTaxonomy={selectedTaxonomy} handleChangeActionMode={handleChangeActionMode} />
        </div>
      )}
    </div>
  );
};
